import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Edit2, Trash2, CheckCircle, AlertCircle, Loader2, Calendar, Users, Eye, Info, ArrowRight } from 'lucide-react';
import { authenticatedFetch, getUser } from '@/lib/auth';
import { useConfirm } from './ui';
import { PedagogicalContextDisplay } from './PedagogicalContextDisplay';
import HelpTooltip from './help/HelpTooltip';

interface CourseInstance {
  id: string;
  name: string;
  exam_set_id: string;
  exam_title: string;
  study_plan_id: string;
  study_plan_title: string;
  ects_credits: number;
  member_id: string;
  organization_name: string;
  // v6.6.2: api_link/api_enabled fjernet
  start_date: string | null;
  end_date: string | null;
  max_candidates: number | null;
  status: string;
  created_at: string;
  session_count: number;
  // Task #133: avledet backend-verdi — alle kandidater sensurert
  all_graded?: boolean;
  candidate_count?: number;
  waitlist_count?: number;
}

interface ExamSet {
  id: string;
  title: string;
  status: string;
  study_plan_id?: string;
  study_plan_title?: string;
  ects_credits?: number;
}

export default function CourseInstanceManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirm } = useConfirm();
  const [instances, setInstances] = useState<CourseInstance[]>([]);
  const [examSets, setExamSets] = useState<ExamSet[]>([]);
  const [members, setMembers] = useState<{ id: string; organization_name: string }[]>([]);
  // Task #133: filter som skiller aktive kurs fra fullførte (arkiv)
  const [viewFilter, setViewFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Get current user info for role-based filtering
  const user = getUser();
  const isMemberUser = user?.role === 'member';
  const userMemberId = user?.member_id;

  const [formData, setFormData] = useState({
    name: '',
    exam_set_id: '',
    member_id: '',
    start_date: '',
    end_date: '',
    max_candidates: '',
    status: 'active',
    exam_window_start: '',
    exam_window_end: '',
    location: '',
    description: '',
    visible_on_kompetanseheving: false,
    show_registered_count: false,
    registration_fee_nok: '0',
    self_cancel_deadline_days: '30',
    wp_course_id: '',
    late_cancellation_fee_nok: '0',
    late_cancellation_fee_active: false,
    no_show_fee_active: false,
    location_type: '',
    city: '',
    meeting_url: '',
    meeting_instructions: '',
    // Task #325 (2026-07-03): kategorier per kurs — overstyrer studieplan-forslag
    categories: [] as string[],
    // v6.6.1 — godkjenningsmodus for kompetanseheving.no-påmeldinger
    approval_mode: 'auto' as 'auto' | 'hybrid' | 'manual'
  });

  // Auto-set member_id for member users on mount
  useEffect(() => {
    if (isMemberUser && userMemberId) {
      setFormData(prev => ({ ...prev, member_id: userMemberId }));
    }
  }, [isMemberUser, userMemberId]);

  // Check for exam_set_id query param (from ExamDetail "Opprett kursinstans" button)
  useEffect(() => {
    const examSetId = searchParams.get('exam_set_id');
    if (examSetId && examSets.length > 0) {
      // Pre-fill form with selected exam set
      const selectedExam = examSets.find(e => e.id === examSetId);
      if (selectedExam) {
        setFormData(prev => ({
          ...prev,
          exam_set_id: examSetId,
          name: selectedExam.title,
          // Auto-set member_id for member users
          member_id: isMemberUser && userMemberId ? userMemberId : prev.member_id
        }));
        setShowForm(true);
        // Clear the query param to avoid re-opening on refresh
        setSearchParams({});
      }
    }
  }, [searchParams, examSets, isMemberUser, userMemberId]);

  useEffect(() => {
    fetchInstances();
    fetchExamSets();
    fetchMembers();
  }, []);

  const fetchInstances = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/admin/course-instances');
      const data = await response.json();

      if (data.success) {
        setInstances(data.instances);
      } else {
        setError(data.message || 'Kunne ikke laste kursinstanser');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchExamSets = async () => {
    try {
      const response = await authenticatedFetch('/api/exams/list');
      const data = await response.json();
      if (data.success) {
        // Show all exam sets, not just published - admins may want to set up course instances early
        // Sort by status (published first) then by title
        const sortedExams = data.exam_sets.sort((a: ExamSet, b: ExamSet) => {
          if (a.status === 'published' && b.status !== 'published') return -1;
          if (a.status !== 'published' && b.status === 'published') return 1;
          return (a.title || '').localeCompare(b.title || '');
        });
        setExamSets(sortedExams);
      }
    } catch (err) {
      console.error('Failed to fetch exam sets:', err);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await authenticatedFetch('/api/admin/members');
      const data = await response.json();
      if (data.success) {
        setMembers(data.members);
      }
    } catch (err) {
      console.error('Failed to fetch members:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // datetime-local input er tidssone-løs. Browseren tolker den som LOKAL tid
      // når vi wrapper i new Date(). toISOString() gir oss UTC. Slik unngås
      // 2t-drift i norsk sommertid (der 20:30 lokal = 18:30 UTC).
      const localDateTimeToUtc = (val: string) => {
        if (!val) return null;
        try {
          return new Date(val).toISOString();
        } catch {
          return null;
        }
      };
      const payload = {
        ...formData,
        max_candidates: formData.max_candidates ? parseInt(formData.max_candidates) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        exam_window_start: localDateTimeToUtc(formData.exam_window_start),
        exam_window_end: localDateTimeToUtc(formData.exam_window_end),
        location: formData.location || null,
        description: formData.description || null,
        registration_fee_nok: parseFloat(formData.registration_fee_nok) || 0,
        self_cancel_deadline_days: parseInt(formData.self_cancel_deadline_days) || 30,
        wp_course_id: formData.wp_course_id ? parseInt(formData.wp_course_id) : null,
        late_cancellation_fee_nok: parseFloat(formData.late_cancellation_fee_nok) || 0,
        late_cancellation_fee_active: formData.late_cancellation_fee_active,
        no_show_fee_active: formData.no_show_fee_active,
        show_registered_count: formData.show_registered_count,
        location_type: formData.location_type || null,
        city: formData.city || null,
        meeting_url: formData.meeting_url || null,
        meeting_instructions: formData.meeting_instructions || null,
        categories: formData.categories.length > 0 ? formData.categories : null,
        approval_mode: formData.approval_mode
      };

      const url = editingId
        ? `/api/admin/course-instances/${editingId}`
        : '/api/admin/course-instances';
      const method = editingId ? 'PATCH' : 'POST';

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(editingId ? 'Kursinstans oppdatert' : 'Kursinstans opprettet');
        setShowForm(false);
        setEditingId(null);
        resetForm();
        fetchInstances();
      } else {
        setError(data.message || 'Kunne ikke lagre kursinstans');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = (instance: CourseInstance) => {
    setEditingId(instance.id);

    // Format dates for input type="date" (requires YYYY-MM-DD format)
    const formatDate = (date: string | null) => {
      if (!date) return '';
      // If date includes time (ISO format), extract just the date part
      return date.split('T')[0];
    };

    const formatDateTime = (date: string | null) => {
      if (!date) return '';
      // For input type="datetime-local" format YYYY-MM-DDTHH:MM
      const d = new Date(date);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    };

    const anyInst = instance as any;
    setFormData({
      name: instance.name,
      exam_set_id: instance.exam_set_id,
      member_id: instance.member_id,
      start_date: formatDate(instance.start_date),
      end_date: formatDate(instance.end_date),
      max_candidates: instance.max_candidates?.toString() || '',
      status: instance.status,
      exam_window_start: formatDateTime(anyInst.exam_window_start),
      exam_window_end: formatDateTime(anyInst.exam_window_end),
      location: anyInst.location || '',
      description: anyInst.description || '',
      visible_on_kompetanseheving: anyInst.visible_on_kompetanseheving === true,
      show_registered_count: anyInst.show_registered_count === true,
      registration_fee_nok: (anyInst.registration_fee_nok || 0).toString(),
      self_cancel_deadline_days: (anyInst.self_cancel_deadline_days || 30).toString(),
      wp_course_id: anyInst.wp_course_id?.toString() || '',
      late_cancellation_fee_nok: (anyInst.late_cancellation_fee_nok || 0).toString(),
      late_cancellation_fee_active: anyInst.late_cancellation_fee_active === true,
      no_show_fee_active: anyInst.no_show_fee_active === true,
      location_type: anyInst.location_type || '',
      city: anyInst.city || '',
      meeting_url: anyInst.meeting_url || '',
      meeting_instructions: anyInst.meeting_instructions || '',
      categories: Array.isArray(anyInst.categories) ? anyInst.categories : [],
      approval_mode: (anyInst.approval_mode as 'auto' | 'hybrid' | 'manual') || 'auto'
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Slett kursinstans',
      message: 'Er du sikker på at du vil slette denne kursinstansen? Dette vil også slette alle tilhørende prøvesesjoner.',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      setError('');
      const response = await authenticatedFetch(`/api/admin/course-instances/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Kursinstans slettet');
        fetchInstances();
      } else {
        setError(data.message || 'Kunne ikke slette kursinstans');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      exam_set_id: '',
      member_id: isMemberUser && userMemberId ? userMemberId : '',
      start_date: '',
      end_date: '',
      max_candidates: '',
      status: 'active',
      exam_window_start: '',
      exam_window_end: '',
      location: '',
      description: '',
      visible_on_kompetanseheving: false,
      show_registered_count: false,
      registration_fee_nok: '0',
      self_cancel_deadline_days: '30',
      wp_course_id: '',
      late_cancellation_fee_nok: '0',
      late_cancellation_fee_active: false,
      no_show_fee_active: false,
      location_type: '',
      city: '',
      meeting_url: '',
      meeting_instructions: '',
      categories: [],
      approval_mode: 'auto'
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-kf-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Kursinstanser</h2>
          <p className="text-sm text-gray-600 mt-1">
            Administrer avsluttende prøver og kursgjennomføringer
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
            setEditingId(null);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700"
        >
          <Plus className="h-5 w-5" />
          Ny kursinstans
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          <span>{success}</span>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold">
            {editingId ? 'Rediger kursinstans' : 'Opprett kursinstans'}
          </h3>

          {editingId && (
            <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-kf-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="text-kf-blue-900 font-medium mb-1">
                  Dette skjemaet redigerer kun kursets grunninformasjon
                </p>
                <p className="text-kf-blue-800 mb-2">
                  Navn, prøvesett, tidspunkt, sted, kompetanseheving-innstillinger og API-tilgang.
                  Kandidater, sensorer, vurderingsoppsett og sensur administreres på kursets detaljside.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    navigate(`/course-instances/${editingId}`);
                  }}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-kf-blue-700 hover:text-kf-blue-900 underline"
                >
                  Åpne kursets detaljside
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instansnavn *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                placeholder="f.eks. Geopolitikk - Vår 2025"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prøvesett *
              </label>
              <select
                required
                value={formData.exam_set_id}
                onChange={(e) => setFormData({ ...formData, exam_set_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
              >
                <option value="">Velg prøvesett...</option>
                {examSets.map(exam => (
                  <option key={exam.id} value={exam.id}>
                    {exam.status !== 'published' ? `[${exam.status === 'draft' ? 'Utkast' : exam.status}] ` : ''}
                    {exam.study_plan_title ? `${exam.study_plan_title} → ${exam.title}` : exam.title}
                    {exam.ects_credits ? ` (${exam.ects_credits} KP)` : ''}
                  </option>
                ))}
              </select>
              {examSets.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Ingen prøvesett funnet. Opprett et prøvesett i Prøvesystem-fanen først.
                </p>
              )}

              {/* Pedagogisk kontekst — vises når prøvesett er valgt */}
              {formData.exam_set_id && (
                <PedagogicalContextDisplay examSetId={formData.exam_set_id} />
              )}
            </div>

            {/* Hide member dropdown for member users - auto-set from their profile */}
            {!isMemberUser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Medlemsorganisasjon *
                </label>
                <select
                  required
                  value={formData.member_id}
                  onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                >
                  <option value="">Velg organisasjon...</option>
                  {members.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.organization_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {isMemberUser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Din organisasjon
                </label>
                <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                  {members.find(m => m.id === userMemberId)?.organization_name || 'Laster...'}
                </div>
              </div>
            )}

            {/* v6.6.2 (2026-09-26): API-tilgang/API-lenke-felt fjernet.
                LMS-integrasjoner (Moodle/TutorLMS/LearnDash/Xtramile/MentorKit) og
                kompetanseheving.no-flyten dekker påmeldings- og eksamens-behovene. */}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maks antall kandidater
              </label>
              <input
                type="number"
                min="1"
                value={formData.max_candidates}
                onChange={(e) => setFormData({ ...formData, max_candidates: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                placeholder="La stå tom for ubegrenset"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Startdato
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sluttdato
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
              >
                <option value="active">Aktiv</option>
                <option value="completed">Fullført</option>
                <option value="cancelled">Kansellert</option>
              </select>
            </div>

            {/* === Eksamen og sted === */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Eksamen og sted</h3>
            </div>

            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                Eksamen starter
                <HelpTooltip slug="exam-window" size="sm" />
              </label>
              <input
                type="datetime-local"
                value={formData.exam_window_start}
                onChange={(e) => setFormData({ ...formData, exam_window_start: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Brukes til SEB-test-purring (48h + 24h før)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Eksamen slutter
              </label>
              <input
                type="datetime-local"
                value={formData.exam_window_end}
                onChange={(e) => setFormData({ ...formData, exam_window_end: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type lokasjon
              </label>
              <select
                value={formData.location_type}
                onChange={(e) => setFormData({ ...formData, location_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
              >
                <option value="">— Velg —</option>
                <option value="physical">Fysisk oppmøte</option>
                <option value="digital">Live online (webinar)</option>
                <option value="elearning">Selvstudium / e-læring</option>
                <option value="blended">Blandet (fysisk + digital)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Brukes til filtrering på kompetanseheving.no</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kommune
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                placeholder="F.eks. Oslo, Bergen, Trondheim"
              />
              <p className="text-xs text-gray-500 mt-1">Norsk kommunenavn (for fylkes-filter)</p>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse / sted (fritekst)
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                placeholder="F.eks. Scandic Helsfyr, Strømsveien 108"
              />
            </div>

            {/* Møte-URL og -instruksjoner: kun relevant for digitale/blandede kurs */}
            {(formData.location_type === 'digital' || formData.location_type === 'blended') && (
              <>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Møte-URL (Teams/Zoom/Meet) — valgfritt
                  </label>
                  <input
                    type="url"
                    value={formData.meeting_url}
                    onChange={(e) => setFormData({ ...formData, meeting_url: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                    placeholder="https://teams.microsoft.com/l/meetup-join/..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Brukes som plassholder <code className="bg-gray-100 px-1 rounded">{'{mote_url}'}</code> i meldinger til kandidater (Kandidat-tab → Send melding).
                  </p>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Møte-instruksjoner — valgfritt
                  </label>
                  <textarea
                    value={formData.meeting_instructions}
                    onChange={(e) => setFormData({ ...formData, meeting_instructions: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                    rows={2}
                    placeholder="F.eks. passord, forberedelse, hvilken plattform"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Plassholder <code className="bg-gray-100 px-1 rounded">{'{mote_instruksjoner}'}</code> i meldinger til kandidater.
                  </p>
                </div>
              </>
            )}

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Beskrivelse
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                rows={3}
                placeholder="Vises på kompetanseheving.no og påmeldingssiden"
              />
            </div>

            {/* === kompetanseheving.no === */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Påmelding via kompetanseheving.no</h3>
            </div>

            <div className="col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.visible_on_kompetanseheving}
                  onChange={(e) => setFormData({ ...formData, visible_on_kompetanseheving: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">
                  Vis på kompetanseheving.no <HelpTooltip slug="kompetanseheving-visibility" size="sm" />
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Aktiverer feltene under (kategorier, gebyr, selvavmeldingsfrist osv.). Endringer synker til kompetanseheving.no innen 15 minutter.
              </p>
            </div>

            {formData.visible_on_kompetanseheving && (
              <>
                {/* v6.6.1 — Godkjenningsmodus (kandidatgodkjenning) */}
                <div className="col-span-2 border border-gray-200 rounded p-4 bg-kf-blue-50/50">
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Godkjenningsmodus for påmeldinger
                  </label>
                  <div className="space-y-2 text-sm">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="approval_mode"
                        value="auto"
                        checked={(formData as any).approval_mode === 'auto' || !(formData as any).approval_mode}
                        onChange={() => setFormData({ ...(formData as any), approval_mode: 'auto' })}
                        className="mt-1"
                      />
                      <span>
                        <strong>Auto-godkjent</strong> — kandidater får plass umiddelbart etter Vipps.
                        Overflødige havner på venteliste.
                      </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="approval_mode"
                        value="hybrid"
                        checked={(formData as any).approval_mode === 'hybrid'}
                        onChange={() => setFormData({ ...(formData as any), approval_mode: 'hybrid' })}
                        className="mt-1"
                      />
                      <span>
                        <strong>Hybrid</strong> — auto-godkjent innen kapasitet. Ved overbook må dere
                        manuelt godkjenne.
                      </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="approval_mode"
                        value="manual"
                        checked={(formData as any).approval_mode === 'manual'}
                        onChange={() => setFormData({ ...(formData as any), approval_mode: 'manual' })}
                        className="mt-1"
                      />
                      <span>
                        <strong>Manuell</strong> — alle påmeldinger krever eksplisitt godkjenning fra
                        arrangør. Overbooking er kun mulig i denne modusen.
                      </span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Kandidater i «venter på godkjenning» får en tydelig e-post om at plassen ikke er bekreftet ennå.
                  </p>
                </div>

                {/* Task #326 (2026-07-03): kategori-velger for kompetanseheving.no
                    per kurs. Overstyrer studieplanens kategori-forslag. */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategorier (kompetanseheving.no)
                  </label>
                  <p className="text-xs text-gray-600 mb-2">
                    Velg kategorier for hvordan dette kurset skal markedsføres på kompetanseheving.no.
                    <strong>Første valgte er primær</strong> for hovedsortering.
                    Lar du dette være tomt, brukes kategoriene fra studieplanen som default.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-gray-50 rounded border border-gray-200">
                    {[
                      'Helse', 'Førstehjelp', 'Ernæring',
                      'Jus', 'Personvern', 'Arbeidsrett',
                      'Ledelse', 'Styrearbeid', 'Prosjektledelse',
                      'HMS', 'Brannvern',
                      'Økonomi', 'Skatt',
                      'IT', 'Programmering',
                      'Kommunikasjon', 'Markedsføring',
                      'Barne- og ungdomsarbeid',
                      'Språk', 'Kultur', 'Hobby'
                    ].map((cat) => {
                      const idx = formData.categories.indexOf(cat);
                      const checked = idx !== -1;
                      const isPrimary = idx === 0;
                      return (
                        <label
                          key={cat}
                          className={`flex items-center gap-2 text-sm p-1 rounded cursor-pointer hover:bg-white ${isPrimary ? 'bg-kf-blue-100' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const newCats = e.target.checked
                                ? [...formData.categories, cat]
                                : formData.categories.filter((c) => c !== cat);
                              setFormData({ ...formData, categories: newCats });
                            }}
                            className="rounded border-gray-300"
                          />
                          <span>{cat}</span>
                          {isPrimary && (
                            <span className="text-xs text-kf-blue-700 font-semibold ml-auto">PRIMÆR</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {formData.categories.length > 0 && (
                    <p className="text-xs text-gray-600 mt-2">
                      Valgt: {formData.categories.join(', ')}
                      {formData.categories.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const cats = [...formData.categories];
                            [cats[0], cats[1]] = [cats[1], cats[0]];
                            setFormData({ ...formData, categories: cats });
                          }}
                          className="ml-2 text-kf-blue-700 hover:underline"
                        >
                          (bytt primær)
                        </button>
                      )}
                    </p>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.show_registered_count}
                      onChange={(e) => setFormData({ ...formData, show_registered_count: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Vis antall påmeldte offentlig
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Default: skjult. Skjult: viser kun totalt antall plasser. Når aktiv: "X av Y plasser fylt" + ev. venteliste-antall.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Påmeldingsgebyr (NOK)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.registration_fee_nok}
                    onChange={(e) => setFormData({ ...formData, registration_fee_nok: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    0 = gratis. Påmeldingsgebyr informeres på påmeldingssiden, men <strong>kreves inn direkte av kursarrangøren</strong> —
                    automatisk Vipps-betaling i systemet kommer i senere versjoner.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Selvavmelding inntil N dager før <HelpTooltip slug="self-cancellation-deadline" size="sm" />
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.self_cancel_deadline_days}
                    onChange={(e) => setFormData({ ...formData, self_cancel_deadline_days: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default: 30. Etter dette må kandidat kontakte vert.</p>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WordPress kurs-ID (valgfri)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.wp_course_id}
                    onChange={(e) => setFormData({ ...formData, wp_course_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                    placeholder="ID fra kompetanseheving.no"
                  />
                  <p className="text-xs text-gray-500 mt-1">For gruppering med andre instanser av samme kurs</p>
                </div>

                {/* === Avbestillings-/no-show-gebyr — relevant for selvpåmelding via kompetanseheving.no === */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Avbestillings-/no-show-gebyr</h3>
                  <p className="text-xs text-gray-600 mb-3">
                    Brukes for å sikre reelle deltakere ved gratis kurs (lav terskel for å melde seg på uten å bruke plassen).
                    <strong> Vises på påmeldingssiden</strong> slik at kandidaten er informert om vilkårene.
                    Kreves inn direkte av kursarrangøren utenom systemet — automatisk Vipps-betaling i systemet kommer i senere versjoner.
                  </p>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gebyr-beløp (NOK)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={formData.late_cancellation_fee_nok}
                    onChange={(e) => setFormData({ ...formData, late_cancellation_fee_nok: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                    placeholder="0 = ingen gebyr"
                  />
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.late_cancellation_fee_active}
                      onChange={(e) => setFormData({ ...formData, late_cancellation_fee_active: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Krev gebyr ved sen avmelding (etter selvavmeldingsfristen)
                    </span>
                  </label>
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.no_show_fee_active}
                      onChange={(e) => setFormData({ ...formData, no_show_fee_active: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Krev gebyr ved no-show (kandidat møtte ikke til eksamen)
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Aktiveres automatisk når vert klikker "Start eksamen" — alle uten avhuket oppmøte flagges.
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700"
            >
              {editingId ? 'Oppdater' : 'Opprett'} kursinstans
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Avbryt
            </button>
          </div>
        </form>
      )}

      {/* Task #133: Arkiv-filter — skiller aktive kurs fra fullførte */}
      {instances.length > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">Vis:</span>
          <button
            type="button"
            onClick={() => setViewFilter('active')}
            className={`px-3 py-1 rounded-full border ${
              viewFilter === 'active'
                ? 'bg-kf-blue-600 text-white border-kf-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Aktive ({instances.filter(i => !i.all_graded).length})
          </button>
          <button
            type="button"
            onClick={() => setViewFilter('archived')}
            className={`px-3 py-1 rounded-full border ${
              viewFilter === 'archived'
                ? 'bg-kf-blue-600 text-white border-kf-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Fullførte ({instances.filter(i => i.all_graded).length})
          </button>
          <button
            type="button"
            onClick={() => setViewFilter('all')}
            className={`px-3 py-1 rounded-full border ${
              viewFilter === 'all'
                ? 'bg-kf-blue-600 text-white border-kf-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Alle ({instances.length})
          </button>
          <span className="text-gray-400 ml-2" title="Fullført = alle gjennomførte prøver er ferdig sensurert. Ikke-møtte kandidater blokkerer ikke.">
            ⓘ «Fullført» = alle prøver sensurert
          </span>
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Instans / Prøve
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Organisasjon
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Periode
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Påmeldte
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Handlinger
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(() => {
              const visible = instances.filter(i => {
                if (viewFilter === 'all') return true;
                if (viewFilter === 'archived') return i.all_graded === true;
                return !i.all_graded;  // 'active'
              });
              if (visible.length === 0) {
                return (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      {instances.length === 0
                        ? 'Ingen kursinstanser funnet. Opprett en for å komme i gang.'
                        : viewFilter === 'archived'
                          ? 'Ingen fullførte kurs enda — kurs flyttes hit når alle påmeldte er sensurert.'
                          : 'Ingen aktive kurs. Bytt til «Fullførte» for å se arkiverte.'}
                    </td>
                  </tr>
                );
              }
              return visible.map((instance) => (
                <tr key={instance.id}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{instance.name}</div>
                      <div className="text-sm text-gray-500">
                        📚 {instance.study_plan_title}
                      </div>
                      <div className="text-xs text-gray-400">
                        {instance.exam_title} • {instance.ects_credits} KP
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {instance.organization_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {instance.start_date ? (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>
                          {new Date(instance.start_date).toLocaleDateString()} -{' '}
                          {instance.end_date
                            ? new Date(instance.end_date).toLocaleDateString()
                            : '?'}
                        </span>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span title={`${(instance as any).candidate_count ?? 0} påmeldte${(instance as any).waitlist_count ? ` + ${(instance as any).waitlist_count} venteliste` : ''} · ${instance.session_count ?? 0} har startet eksamen`}>
                        {(instance as any).candidate_count ?? 0}
                        {instance.max_candidates ? `/${instance.max_candidates}` : ''}
                        {(instance as any).waitlist_count > 0 && (
                          <span className="text-xs text-amber-700 ml-1">+{(instance as any).waitlist_count} venter</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        instance.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : instance.status === 'completed'
                          ? 'bg-kf-blue-100 text-kf-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {instance.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/course-instances/${instance.id}`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#B5651D] text-white rounded-md hover:bg-[#8B4513] font-medium"
                        title="Åpne kurset — kandidater, sensorer, vurderingsoppsett, sensur"
                      >
                        <Eye className="h-4 w-4" />
                        Åpne
                      </button>
                      <button
                        onClick={() => handleEdit(instance)}
                        className="text-gray-600 hover:text-gray-900 p-1.5"
                        title="Rediger kurs-info (navn, prøvesett, tidspunkt, sted)"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(instance.id)}
                        className="text-red-600 hover:text-red-900 p-1.5"
                        title="Slett kursinstans"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
